import { useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { waLink, telLink, track, newIdempotencyKey, EMPLOYMENT_TYPES, CIBIL_RANGES } from "../lib/site";

const STEPS = ["Requirement", "Personal", "Employment", "Financials", "Consent"];

export default function Apply() {
  const { settings, products } = useSite();
  const params = useSearchParams()[0];
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const idemKey = useMemo(newIdempotencyKey, []);
  const [f, setF] = useState({
    loan_type: params.get("product") || "", amount: "", purpose: "",
    name: "", mobile: "", email: "", dob: "", city: "",
    employment: "", employer: "", business_name: "", business_vintage: "",
    income: "", existing_emi: "", cibil: "", turnover: "",
    property_type: "", property_value: "", consent: false,
  });

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const isBusiness = ["Business Loan", "Working Capital Loan"].includes(f.loan_type);
  const isSelfEmployed = f.employment === "Self-Employed";
  const isLAP = f.loan_type === "Loan Against Property";

  const stepValid = useMemo(() => {
    if (step === 0) return f.loan_type && Number(f.amount) > 0;
    if (step === 1) return f.name.trim().length >= 2 && /^\+?[\d\s-]{10,15}$/.test(f.mobile);
    if (step === 2) return !!f.employment;
    if (step === 3) return Number(f.income) > 0;
    if (step === 4) return f.consent;
    return false;
  }, [step, f]);

  const submit = async () => {
    setLoading(true);
    try {
      const utm = new URLSearchParams(window.location.search);
      const { data } = await api.post("/leads", {
        ...f, amount: Number(f.amount), income: Number(f.income),
        existing_emi: Number(f.existing_emi) || 0, turnover: Number(f.turnover) || 0,
        property_value: Number(f.property_value) || 0,
        kind: "application", source: "apply_flow", page: "/apply", idempotency_key: idemKey,
        utm_source: utm.get("utm_source") || "", utm_medium: utm.get("utm_medium") || "",
        utm_campaign: utm.get("utm_campaign") || "", utm_term: utm.get("utm_term") || "", utm_content: utm.get("utm_content") || "",
      });
      track("lead_submit", { lead_id: data.lead_id, product: f.loan_type });
      setDone(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="apply-success" className="container-site py-16 lg:py-24">
        <div className="max-w-xl mx-auto card-site p-8 sm:p-12 text-center">
          <span className="mx-auto w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle2 size={32} /></span>
          <h1 className="mt-6 font-heading text-3xl font-extrabold text-brand-dark">Your enquiry has been submitted successfully</h1>
          <p className="mt-3 text-sm text-brand-muted">Application Reference ID</p>
          <p className="font-heading text-2xl font-extrabold text-brand-red mt-1" data-testid="apply-lead-id">{done.lead_id}</p>
          <p className="mt-5 text-sm text-brand-muted leading-relaxed">A dedicated relationship manager will contact you to discuss potentially suitable lender options. Final approval and terms are determined by the lending partner.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href={waLink(settings, settings?.whatsapp_templates?.default)} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", { lead_id: done.lead_id, product: f.loan_type })} data-testid="apply-success-whatsapp" className="btn-outline !border-[#1FA855]/40 !text-[#1FA855]"><MessageCircle size={15} /> WhatsApp Advisor</a>
            <a href={telLink(settings)} onClick={() => track("phone_click", { lead_id: done.lead_id })} data-testid="apply-success-call" className="btn-outline"><Phone size={15} /> Call Advisor</a>
            <Link to="/" data-testid="apply-success-home" className="btn-primary">Return Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="apply-page" className="container-site py-12 lg:py-16">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <p className="eyebrow">Loan Application</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold text-brand-dark">Start Your Application</h1>
          <p className="mt-3 text-sm text-brand-muted">Takes about 2 minutes. No impact on your credit score.</p>
        </div>

        <div className="flex items-center gap-2 mb-8" data-testid="apply-progress">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors duration-300 ${i <= step ? "bg-brand-red" : "bg-black/10"}`} />
              <p className={`mt-2 text-[10px] font-bold uppercase tracking-wider ${i <= step ? "text-brand-red" : "text-brand-muted/60"}`}>{s}</p>
            </div>
          ))}
        </div>

        <div className="card-site p-6 sm:p-10">
          {step === 0 && (
            <div className="space-y-4" data-testid="apply-step-0">
              <h2 className="font-heading text-xl font-bold text-brand-dark">What do you need?</h2>
              <select data-testid="apply-loan-type" className="input-site" value={f.loan_type} onChange={set("loan_type")} aria-label="Loan type">
                <option value="" disabled>Select Loan Type *</option>
                {(products.length ? products.map((p) => p.name) : []).map((t) => <option key={t} value={t}>{t}</option>)}
                <option value="Other">Other</option>
              </select>
              <input data-testid="apply-amount" type="number" className="input-site" placeholder="Required Amount (₹) *" value={f.amount} onChange={set("amount")} aria-label="Required amount" />
              <input data-testid="apply-purpose" className="input-site" placeholder="Purpose (e.g. wedding, expansion, home purchase)" value={f.purpose} onChange={set("purpose")} aria-label="Purpose" />
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="apply-step-1">
              <h2 className="font-heading text-xl font-bold text-brand-dark">Your details</h2>
              <input data-testid="apply-name" className="input-site" placeholder="Full Name *" value={f.name} onChange={set("name")} aria-label="Full name" />
              <input data-testid="apply-mobile" className="input-site" placeholder="Mobile Number *" value={f.mobile} onChange={set("mobile")} inputMode="tel" aria-label="Mobile number" />
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="apply-email" type="email" className="input-site" placeholder="Email" value={f.email} onChange={set("email")} aria-label="Email" />
                <input data-testid="apply-dob" type="date" className="input-site" value={f.dob} onChange={set("dob")} aria-label="Date of birth" />
              </div>
              <input data-testid="apply-city" className="input-site" placeholder="City" value={f.city} onChange={set("city")} aria-label="City" />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="apply-step-2">
              <h2 className="font-heading text-xl font-bold text-brand-dark">Employment</h2>
              <select data-testid="apply-employment" className="input-site" value={f.employment} onChange={set("employment")} aria-label="Employment type">
                <option value="" disabled>Employment Type *</option>
                {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {!isSelfEmployed && f.employment && (
                <input data-testid="apply-employer" className="input-site" placeholder="Employer / Organization Name" value={f.employer} onChange={set("employer")} aria-label="Employer" />
              )}
      {isSelfEmployed && (
                <>
                  <input data-testid="apply-business-name" className="input-site" placeholder="Business Name" value={f.business_name} onChange={set("business_name")} aria-label="Business name" />
                  <select data-testid="apply-business-vintage" className="input-site" value={f.business_vintage} onChange={set("business_vintage")} aria-label="Business vintage">
                    <option value="">Business Vintage</option>
                    <option>Less than 1 year</option><option>1 – 3 years</option><option>3 – 5 years</option><option>5+ years</option>
                  </select>
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="apply-step-3">
              <h2 className="font-heading text-xl font-bold text-brand-dark">Financial details</h2>
              <input data-testid="apply-income" type="number" className="input-site" placeholder={isBusiness || isSelfEmployed ? "Average Monthly Income (₹) *" : "Net Monthly Income (₹) *"} value={f.income} onChange={set("income")} aria-label="Monthly income" />
              {(isBusiness || isSelfEmployed) && (
                <input data-testid="apply-turnover" type="number" className="input-site" placeholder="Annual Business Turnover (₹)" value={f.turnover} onChange={set("turnover")} aria-label="Annual turnover" />
              )}
              <input data-testid="apply-existing-emi" type="number" className="input-site" placeholder="Existing EMIs (₹/month, if any)" value={f.existing_emi} onChange={set("existing_emi")} aria-label="Existing EMIs" />
              <select data-testid="apply-cibil" className="input-site" value={f.cibil} onChange={set("cibil")} aria-label="CIBIL range">
                <option value="">CIBIL / Credit Score Range</option>
                {CIBIL_RANGES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {isLAP && (
                <>
                  <select data-testid="apply-property-type" className="input-site" value={f.property_type} onChange={set("property_type")} aria-label="Property type">
                    <option value="">Property Type</option>
                    <option>Residential — Self Occupied</option><option>Residential — Rented</option><option>Commercial</option><option>Plot / Land</option>
                  </select>
                  <input data-testid="apply-property-value" type="number" className="input-site" placeholder="Approximate Property Value (₹)" value={f.property_value} onChange={set("property_value")} aria-label="Property value" />
                </>
              )}
              <p className="text-xs text-brand-muted leading-relaxed">Typical documents: PAN, Aadhaar, income proof and bank statements. Your relationship manager will share the exact checklist for your profile.</p>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5" data-testid="apply-step-4">
              <h2 className="font-heading text-xl font-bold text-brand-dark">Review & Consent</h2>
              <div className="rounded-2xl bg-brand-cream p-5 text-sm space-y-2">
                <p><span className="text-brand-muted">Product:</span> <span className="font-bold">{f.loan_type}</span></p>
                <p><span className="text-brand-muted">Amount:</span> <span className="font-bold">₹{Number(f.amount).toLocaleString("en-IN")}</span></p>
                <p><span className="text-brand-muted">Name:</span> <span className="font-bold">{f.name}</span></p>
                <p><span className="text-brand-muted">Mobile:</span> <span className="font-bold">{f.mobile}</span></p>
                <p><span className="text-brand-muted">Employment:</span> <span className="font-bold">{f.employment}</span></p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="apply-consent-label">
                <input type="checkbox" checked={f.consent} onChange={set("consent")} data-testid="apply-consent-checkbox"
                  className="mt-1 w-4 h-4 accent-brand-red" />
                <span className="text-xs text-brand-muted leading-relaxed">{settings.consent_text}</span>
              </label>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} data-testid="apply-back-btn" className="btn-outline !px-5 !py-2.5"><ArrowLeft size={15} /> Back</button>
            ) : <span />}
            {step < 4 ? (
              <button onClick={() => stepValid && setStep(step + 1)} disabled={!stepValid} data-testid="apply-next-btn" className="btn-primary !px-6 !py-2.5">Continue <ArrowRight size={15} /></button>
            ) : (
              <button onClick={submit} disabled={!stepValid || loading} data-testid="apply-submit-btn" className="btn-primary !px-6 !py-2.5">
                {loading ? <Loader2 size={16} className="animate-spin" /> : "Submit Application"}
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-xs text-center text-brand-muted leading-relaxed">{settings.disclaimer}</p>
      </div>
    </div>
  );
}
